'use strict';

/**
 * Learn-more panel: a DOM node moved into a SweetAlert2 drawer — right panel on desktop,
 * bottom sheet under 768px — and put back where it was on close.
 * Shared by the PLP listing tile and the bundle products section.
 */

const customSwal = require('./customSwal');

const MOBILE_QUERY = '(max-width: 767.98px)';
const RETURN_SLOT_ATTRIBUTE = 'data-learn-more-return-slot';

function isMobileLayout() {
	return typeof window.matchMedia === 'function' && window.matchMedia(MOBILE_QUERY).matches;
}

/**
 * Names the SweetAlert2 popup — the element that actually carries role="dialog" — after a
 * heading inside the moved panel. Opt-in: callers that do not pass labelSelector are untouched.
 * @param {HTMLElement} popup - SweetAlert2 popup element
 * @param {HTMLElement} panelRoot - panel node moved into the drawer
 * @param {string} labelSelector - selector of the heading naming the dialog
 * @returns {void}
 */
function applyAccessibleName(popup, panelRoot, labelSelector) {
	if (!popup || !panelRoot) {
		return;
	}
	const labelElement = panelRoot.querySelector(labelSelector);
	if (!labelElement || !labelElement.id) {
		return;
	}
	popup.setAttribute('aria-labelledby', labelElement.id);
}

function buildOptions(config, triggerButton, panelRoot, returnSlot) {
	const mobile = isMobileLayout();
	const drawerClass = config.drawerClass;
	const hasStickyFooter = panelRoot.matches('.learn-more-modal');
	const options = {
		html: panelRoot,
		didOpen: function onOpen(popup) {
			triggerButton.setAttribute('aria-expanded', 'true');
			if (window.lvzTechnicalData && typeof window.lvzTechnicalData.initTechnicalDataIn === 'function') {
				window.lvzTechnicalData.initTechnicalDataIn(panelRoot, { force: true });
			}
			if (window.lvzProductPhotoGallery && typeof window.lvzProductPhotoGallery.initIn === 'function') {
				window.lvzProductPhotoGallery.initIn(panelRoot, { force: true });
			}
			if (config.labelSelector) {
				applyAccessibleName(popup, panelRoot, config.labelSelector);
			}
		},
		didClose: function onClose() {
			if (returnSlot && returnSlot.parentNode && panelRoot) {
				panelRoot.hidden = true;
				returnSlot.parentNode.insertBefore(panelRoot, returnSlot);
				returnSlot.parentNode.removeChild(returnSlot);
			}
			triggerButton.setAttribute('aria-expanded', 'false');
		},
		customClass: {
			container: 'sa-right-panel-container ' + drawerClass,
			popup:
				'sa-right-panel-popup ' +
				drawerClass +
				'__popup' +
				(mobile ? '' : ' ' + drawerClass + '__popup--desktop') +
				(hasStickyFooter ? ' ' + drawerClass + '__popup--sticky-footer' : ''),
			htmlContainer: 'sa-right-panel-html ' + drawerClass + '__html' + (hasStickyFooter ? ' ' + drawerClass + '__html--sticky-footer' : ''),
		},
	};

	if (mobile) {
		options.position = 'bottom';
		options.grow = 'row';
		options.width = '100%';
		options.showClass = { popup: 'sa2-slide-up-in sa2-anim-faster' };
		options.hideClass = { popup: 'sa2-slide-down-out sa2-anim-faster' };
	}

	return options;
}

/**
 * @param {Object} config - selectors and drawer class
 * @param {string} config.wrapperSelector - element wrapping trigger and panel
 * @param {string} config.openSelector - trigger button
 * @param {string} config.panelSelector - panel node moved into the drawer
 * @param {string} config.drawerClass - CSS class prefix applied to the SweetAlert2 nodes
 * @param {string} [config.labelSelector] - selector of the panel heading that names the dialog
 * @returns {void}
 */
function delegatePanel(config) {
	document.addEventListener('click', function onDocumentClick(event) {
		const rawTarget = event.target;
		const target = rawTarget && rawTarget.nodeType === Node.TEXT_NODE ? rawTarget.parentElement : rawTarget;
		if (!target || typeof target.closest !== 'function') {
			return;
		}
		const triggerButton = target.closest(config.openSelector);
		if (!triggerButton) {
			return;
		}
		const wrapper = triggerButton.closest(config.wrapperSelector);
		if (!wrapper) {
			return;
		}
		const panelRoot = wrapper.querySelector(config.panelSelector);
		if (!panelRoot) {
			return;
		}
		event.preventDefault();
		if (customSwal.Swal.isVisible()) {
			return;
		}
		const returnSlot = document.createElement('span');
		returnSlot.setAttribute(RETURN_SLOT_ATTRIBUTE, 'true');
		returnSlot.hidden = true;
		wrapper.insertBefore(returnSlot, panelRoot);
		panelRoot.hidden = false;
		customSwal.Swal.fire(customSwal.rightPanelOptions(buildOptions(config, triggerButton, panelRoot, returnSlot)));
	});
}

module.exports = {
	delegatePanel: delegatePanel,
};
